import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "hooks/useQuery";

import { GifList, IUserList } from "components/feed/GifList";
import { useNicheGifs, useBoostedGifs } from "queries";

import { IGif, INicheMap } from "services/types";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { filterUsersList } from "utils/filterUsersList";
import { routes } from "routes";
import { ViewRegion } from "utils/metrics";
import { mixWithBoosted } from "utils/boost";
import { debugLoadingGifs } from "utils/bugSnagHelper";
import "./NicheGifList.scss";

const PAGE_SIZE = 30;

interface IProps {
  id: string;
}

export const NicheGifList = (props: IProps) => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [totalGifs, setTotalGifs] = useState<number>(0);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [topic, setTopic] = useState<string>("a");
  const [order, setOrder] = useState("trending");
  const [tiles, setTiles] = useState<boolean>(true);
  const [niches, setNiches] = useState<INicheMap>({});
  const navigate = useNavigate();
  const query = useQuery();

  const { isSuccess, isLoading, isError, data, error } = useNicheGifs({
    id: props.id,
    page,
    type: topic === "a" ? undefined : topic,
    order,
    count: PAGE_SIZE,
  });

  const { data: boostedPostData } = useBoostedGifs();

  const reload = useCallback(() => {
    setTotalGifs(0);
    setGifs([]);
    setPage(1);
  }, []);

  const changeTopic = useCallback((value: string) => {
    console.debug(`[NicheGifList] topic changed to ${value}`);
    setTopic(value);
    reload();
  }, [reload]);

  const changeOrder = useCallback((value: string) => {
    console.debug(`[NicheGifList] order changed to ${value}`);
    navigate(routes.niche(props.id, value));
    setOrder(value);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const changeMode = useCallback((mode: boolean) => {
    console.debug(`[NicheGifList] tiles changed to ${mode}`);
    setTiles(mode);
  }, []);

  const fetchMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const rel = useMemo(() => `niche:${props.id}`, [props.id]);

  useEffect(() => {
    reload();
  }, [props.id, reload]);

  // Process the gif query.
  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setGifs(prev => {
        const filteredGifs = filterGifsList(prev, data.gifs);
        const gifs = mixWithBoosted({
          gifs: filteredGifs,
          boostedPostData: boostedPostData?.gifs || [],
          tiles
        });

        return gifs;
      });

      setPages(data.pages);
      setTotalGifs(data.total);
      setUsers(prev => filterUsersList(prev, [...data.users, ...(boostedPostData?.users || [])]));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [data, isSuccess, boostedPostData, tiles]);

  useEffect(() => {
    const orderParam = query.get("order");

    if (orderParam) {
      changeOrder(orderParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: debug 'error loading gif'
  useEffect(() => {
    if (isError) {
      debugLoadingGifs(error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  return (
    <GifList
      className="nicheGifList"
      topic={topic}
      order={order}
      ordering
      tiles={tiles}
      gifs={gifs}
      users={users}
      niches={niches}
      totalLength={totalGifs}
      hasMore={page < pages}
      changeTopic={changeTopic}
      changeOrder={changeOrder}
      changeMode={changeMode}
      fetchMore={fetchMore}
      isError={isError}
      isLoading={isLoading}
      source={ViewRegion.Niche}
      rel={rel}
    />
  );
};
